import React, { useMemo, useState } from 'react';
import {
  MdArrowBack, MdRepeat, MdMusicNote, MdLiveTv, MdFitnessCenter,
  MdShoppingBag, MdCloud, MdMoreHoriz, MdPause, MdPlayArrow, MdShield,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp, maskAmount } from '../contexts/AppContext';
import { denormalizeTransactions } from '../utils/denormalizeTransactions';
import { classifyRecurringKind, recurringClass } from '../utils/classifyRecurring';

// Heuristic: a subscription is a debit that recurs at the same merchant
// at a roughly monthly cadence with consistent amounts. We keep this
// simple — group by description, require ≥2 occurrences in 90 days, take
// the median amount as the monthly cost.
const FALLBACK_SUBS = [
  { id: 'netflix',   name: 'Netflix',           cost: 16.49,  cadence: 'Monthly', icon: MdLiveTv,         tint: '#D32F2F' },
  { id: 'spotify',   name: 'Spotify Premium',   cost: 11.99,  cadence: 'Monthly', icon: MdMusicNote,      tint: '#2FBF00' },
  { id: 'icloud',    name: 'Apple iCloud+',     cost: 3.99,   cadence: 'Monthly', icon: MdCloud,          tint: '#946E9F' },
  { id: 'goodlife',  name: 'GoodLife Fitness',  cost: 49.99,  cadence: 'Monthly', icon: MdFitnessCenter,  tint: '#7C5636' },
  { id: 'prime',     name: 'Amazon Prime',      cost: 10.99,  cadence: 'Monthly', icon: MdShoppingBag,    tint: '#448889' },
];

function classifyItem(item) {
  const kind = classifyRecurringKind(item.name);
  const cls = recurringClass(kind);
  return { ...item, kind, class: cls };
}

function detectSubscriptions(profile) {
  const classified = (items) =>
    items.map(classifyItem).filter(s => s.class !== 'exclude');

  if (!profile?.accounts) return classified(FALLBACK_SUBS);

  // Pull debits across all accounts.
  const all = [];
  Object.values(profile.accounts).forEach(acct => {
    if (!acct?.transactions) return;
    denormalizeTransactions(acct.transactions).forEach(t => {
      if (t.amount < 0 && t.description) all.push(t);
    });
  });
  if (all.length < 4) return classified(FALLBACK_SUBS);

  // Group by lowercase description; keep groups with ≥2 occurrences.
  const groups = {};
  all.forEach(t => {
    const key = (t.description || '').toLowerCase().trim();
    if (!key) return;
    (groups[key] = groups[key] || []).push(t);
  });
  const subs = Object.entries(groups)
    .filter(([, txs]) => txs.length >= 2)
    .map(([key, txs]) => {
      const amounts = txs.map(t => Math.abs(t.amount)).sort((a, b) => a - b);
      const median = amounts[Math.floor(amounts.length / 2)];
      // Range check — wildly varying amounts probably aren't a sub.
      const drift = (amounts[amounts.length - 1] - amounts[0]) / Math.max(1, median);
      if (drift > 0.5) return null;
      // Cadence gate (mirrors backend computeSubscriptions in lib/insights.js):
      // a charge is only "recurring" if the gaps between charges match a known
      // frequency band. Sort by date, average the day-gaps, and drop anything
      // that doesn't land in a band — this keeps one-time / coincidental
      // charges out of the recurring list.
      const dates = txs
        .map(t => new Date(t.date).getTime())
        .filter(ms => !Number.isNaN(ms))
        .sort((a, b) => a - b);
      if (dates.length < 2) return null;
      const gaps = [];
      for (let i = 1; i < dates.length; i++) gaps.push((dates[i] - dates[i - 1]) / 86400000);
      const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
      let cadence = null;
      if (avgGap >= 25 && avgGap <= 35) cadence = 'Monthly';
      else if (avgGap >= 6 && avgGap <= 8) cadence = 'Weekly';
      else if (avgGap >= 12 && avgGap <= 16) cadence = 'Bi-weekly';
      else if (avgGap >= 85 && avgGap <= 95) cadence = 'Quarterly';
      else return null;
      const raw = {
        id: key,
        name: txs[0].description.replace(/\s+/g, ' ').trim().slice(0, 28),
        cost: median,
        cadence,
        icon: MdRepeat,
        tint: '#946E9F',
      };
      return classifyItem(raw);
    })
    .filter(Boolean)
    .filter(s => s.class !== 'exclude')
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 12);

  return subs.length ? subs : classified(FALLBACK_SUBS);
}

const fmtCad = (v) => `$${Number(v || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Header total drops cents to match the other top-of-screen balance heroes.
const fmtCadInt = (v) => `$${Math.round(Number(v || 0)).toLocaleString('en-CA')}`;

const SubscriptionsScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const { hideBalances } = useApp();
  const detected = useMemo(() => detectSubscriptions(loadedProfileData?.profile), [loadedProfileData]);
  const [expandedId, setExpandedId] = useState(null);

  // Per-id state: { paused: bool, essentialOverride: bool|null }. The
  // override lets the user move something the classifier mis-bucketed
  // between the two sections without us having to maintain a perfect ruleset.
  const [subState, setSubState] = useState({});
  const isPaused    = (id) => !!subState[id]?.paused;
  const getClass    = (sub) => {
    const override = subState[sub.id]?.essentialOverride;
    if (override === true) return 'essential';
    if (override === false) return 'subscription';
    return sub.class;
  };
  const isEssential = (sub) => getClass(sub) === 'essential';

  const togglePause     = (id) =>
    setSubState(prev => ({ ...prev, [id]: { ...prev[id], paused: !prev[id]?.paused } }));
  const toggleEssential = (sub) =>
    setSubState(prev => ({
      ...prev,
      [sub.id]: { ...prev[sub.id], essentialOverride: !isEssential(sub) },
    }));

  const subs = detected;
  const subscriptionItems = subs.filter(s => !isEssential(s));
  const essentialItems    = subs.filter(s => isEssential(s));

  const pausableCount      = subscriptionItems.filter(s => !isPaused(s.id)).length;
  const pausedCount        = subs.filter(s => isPaused(s.id)).length;
  const pauseAllNonEssential = () =>
    setSubState(prev => {
      const next = { ...prev };
      subscriptionItems.forEach(s => { next[s.id] = { ...next[s.id], paused: true }; });
      return next;
    });
  // Symmetric to the bulk pause: clear the paused flag on everything so a
  // one-tap pause is always reversible with a one-tap resume (the per-row
  // Resume button lives inside an expanded row, which isn't obvious).
  const resumeAll = () =>
    setSubState(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        if (next[id]?.paused) next[id] = { ...next[id], paused: false };
      });
      return next;
    });

  // Totals reflect what's currently active — paused subs don't count.
  const activeSubs    = subs.filter(s => !isPaused(s.id));
  const monthlyTotal  = activeSubs.reduce((s, x) => s + (x.cost || 0), 0);
  const yearlyTotal   = monthlyTotal * 12;
  const pausedSavings = subs.filter(s => isPaused(s.id)).reduce((s, x) => s + (x.cost || 0), 0);

  // Per-section subtotals (active only).
  const subMonthly = subscriptionItems.filter(s => !isPaused(s.id)).reduce((s, x) => s + (x.cost || 0), 0);
  const essMonthly = essentialItems.filter(s => !isPaused(s.id)).reduce((s, x) => s + (x.cost || 0), 0);

  const renderRow = (s, { pausable }) => {
    const Icon = s.icon || MdRepeat;
    const isOpen  = expandedId === s.id;
    const paused  = isPaused(s.id);
    const essential = isEssential(s);
    return (
      <div
        key={s.id}
        className="bh-recent-row"
        data-testid={`sub-row-${s.id}`}
        data-pausable={pausable}
        style={{
          flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer',
          opacity: paused ? 0.55 : 1,
        }}
        onClick={() => setExpandedId(isOpen ? null : s.id)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <span
            style={{
              width: 36, height: 36, background: paused ? 'var(--bh-gray)' : s.tint, color: 'var(--bh-white)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, borderRadius: 'var(--radius-md)',
            }}
          >
            <Icon size={18} />
          </span>
          <div className="bh-recent-row-info" style={{ flex: 1, minWidth: 0 }}>
            <span className="bh-recent-row-name" style={{ textDecoration: paused ? 'line-through' : 'none' }}>
              {s.name}
            </span>
            <span className="bh-recent-row-date" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {s.cadence}
              {essential && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  background: 'rgba(0, 43, 26, 0.08)', color: 'var(--td-premium)',
                  padding: '2px 7px', font: 'var(--bh-caption)', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  borderRadius: 'var(--radius-full)',
                }}>
                  <MdShield size={11} /> Essential
                </span>
              )}
              {paused && (
                <span style={{
                  background: 'var(--td-amber)', color: 'var(--bh-white)',
                  padding: '2px 7px', font: 'var(--bh-caption)', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  borderRadius: 'var(--radius-full)',
                }}>
                  Paused
                </span>
              )}
            </span>
          </div>
          <div className="bh-recent-row-meta">
            <span className="bh-recent-row-amount">{maskAmount(fmtCad(s.cost), hideBalances)}</span>
            <span className="bh-recent-row-status" style={{ color: 'var(--bh-gray)' }}>
              {maskAmount(fmtCad(s.cost * 12), hideBalances)}/yr
            </span>
          </div>
        </div>

        {isOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)', marginTop: 'var(--space-2xs)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-2xs)' }}>
              {pausable && (
                <button
                  className={paused ? 'bh-btn-primary' : 'bh-btn-outline-dark'}
                  style={{ flex: 1 }}
                  onClick={(e) => { e.stopPropagation(); togglePause(s.id); }}
                >
                  {paused
                    ? <><MdPlayArrow size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Resume</>
                    : <><MdPause size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Pause</>}
                </button>
              )}
              <button
                className="bh-btn-outline-dark"
                style={{ flex: 1 }}
                onClick={(e) => { e.stopPropagation(); toggleEssential(s); }}
              >
                Mark {essential ? 'non-essential' : 'essential'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2xs)' }}>
              <button
                className="bh-btn-outline-dark"
                style={{ flex: 1 }}
                onClick={(e) => { e.stopPropagation(); onNavigate('chat'); }}
              >
                Ask IVA about this
              </button>
              <button
                className="bh-btn-destructive"
                style={{ flex: 1 }}
                onClick={(e) => { e.stopPropagation(); /* future: cancel-flow */ }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('services'))} aria-label="Back to services">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Subscriptions</h1>
        </div>
        <MdRepeat size={22} style={{ color: 'var(--bh-black)' }} />
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ alignItems: 'center', gap: 'var(--space-2xs)' }}>
          <span style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
            Monthly recurring
          </span>
          <span style={{ font: 'var(--bh-display)', fontWeight: 800, color: 'var(--bh-black)', letterSpacing: '-0.02em' }}>
            {maskAmount(fmtCadInt(monthlyTotal), hideBalances)}
          </span>
          <span style={{ font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
            About {maskAmount(fmtCadInt(yearlyTotal), hideBalances)} a year across {activeSubs.length} active item{activeSubs.length === 1 ? '' : 's'}.
          </span>
          {pausedSavings > 0 && (
            <span style={{ font: 'var(--bh-caption)', color: 'var(--td-green)', fontWeight: 700 }}>
              Pausing saves {maskAmount(fmtCad(pausedSavings), hideBalances)}/month
            </span>
          )}
        </div>

        {/* Bulk pause card — only shown when there's something pause-worthy. */}
        {pausableCount > 0 && (
          <div className="bh-form-card" style={{ gap: 'var(--space-2xs)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <span style={{
                width: 36, height: 36, background: 'var(--td-amber)', color: 'var(--bh-white)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, borderRadius: 'var(--radius-md)',
              }}>
                <MdPause size={20} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: 'var(--bh-body)', fontWeight: 700, color: 'var(--bh-black)' }}>
                  Quick pause
                </div>
                <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                  {pausableCount} non-essential {pausableCount === 1 ? 'subscription' : 'subscriptions'} ·{' '}
                  {maskAmount(fmtCad(subscriptionItems.filter(s => !isPaused(s.id)).reduce((sum, s) => sum + s.cost, 0)), hideBalances)}/mo
                </div>
              </div>
            </div>
            <button className="bh-btn-primary-alt" style={{ width: '100%' }} onClick={pauseAllNonEssential}>
              Pause all non-essential
            </button>
          </div>
        )}

        {/* Resume card — appears whenever anything is paused, so unpausing is
            never buried inside a row. Mirrors the Quick pause card. */}
        {pausedCount > 0 && (
          <div className="bh-form-card" style={{ gap: 'var(--space-2xs)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <span style={{
                width: 36, height: 36, background: 'var(--td-green)', color: 'var(--bh-white)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, borderRadius: 'var(--radius-md)',
              }}>
                <MdPlayArrow size={20} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: 'var(--bh-body)', fontWeight: 700, color: 'var(--bh-black)' }}>
                  {pausedCount} paused
                </div>
                <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                  Saving {maskAmount(fmtCad(pausedSavings), hideBalances)}/mo · tap a row to resume just one
                </div>
              </div>
            </div>
            <button className="bh-btn-outline-dark" style={{ width: '100%' }} onClick={resumeAll}>
              Resume all paused
            </button>
          </div>
        )}

        {/* Subscriptions section — discretionary, pausable */}
        {subscriptionItems.length > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Subscriptions</h2>
            <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)', marginBottom: 'var(--space-xs)' }}>
              {maskAmount(fmtCad(subMonthly), hideBalances)}/mo · {maskAmount(fmtCad(subMonthly * 12), hideBalances)}/yr
            </div>
            <div className="bh-recent-list">
              {subscriptionItems.map(s => renderRow(s, { pausable: true }))}
            </div>
          </section>
        )}

        {/* Essential monthly bills — required for living, not pausable */}
        {essentialItems.length > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Essential monthly bills</h2>
            <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)', marginBottom: 'var(--space-xs)' }}>
              Recurring obligations · {maskAmount(fmtCad(essMonthly), hideBalances)}/mo · {maskAmount(fmtCad(essMonthly * 12), hideBalances)}/yr
            </div>
            <div className="bh-recent-list">
              {essentialItems.map(s => renderRow(s, { pausable: false }))}
            </div>
          </section>
        )}

        <div className="bh-info-box">
          <MdMoreHoriz size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          We detect subscriptions by spotting repeat charges. Some annual subscriptions
          may not show up until they re-bill. Tap one to manage it.
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsScreen;
